import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar as CalendarIcon, Filter, Clock, RotateCcw, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { db } from '@/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { createLog } from '@/utils/createLog';

export default function Timesheet() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("all");
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
      const isSuperAdmin = storedUser.role === 'super_admin' || storedUser.role === 'superadmin';
      if (!isSuperAdmin) {
        createLog({
          ticket: { id: "N/A", ticketNumber: "N/A" },
          action: "unauthorized_access",
          message: `User attempted to access unauthorized page: Timesheet`,
          user: storedUser,
        });
        alert("You do not have access to this page");
        navigate("/dashboard", { replace: true });
        return;
      }
      fetchData();
    }
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Projects
      const projectsSnap = await getDocs(query(collection(db, 'projects')));
      const projectsList = projectsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsList);

      // Fetch Resolved Tickets
      const ticketsQuery = query(
        collection(db, 'tickets'),
        where('status', '==', 'Resolved')
      );
      const ticketsSnap = await getDocs(ticketsQuery);
      const ticketsList = ticketsSnap.docs.map(doc => {
        const data = doc.data();
        let resolvedDate = new Date();
        if (data.updated_at?.toDate) {
          resolvedDate = data.updated_at.toDate();
        } else if (data.updated_at) {
          resolvedDate = new Date(data.updated_at);
        } else if (data.created_at?.toDate) {
          resolvedDate = data.created_at.toDate();
        } else if (data.created_at) {
          resolvedDate = new Date(data.created_at);
        }

        return {
          id: doc.id,
          ...data,
          resolvedDate
        };
      });

      // Sort in memory by resolved date desc
      ticketsList.sort((a, b) => b.resolvedDate - a.resolvedDate);
      setTickets(ticketsList);
    } catch (error) {
      console.error("Error fetching timesheet data:", error);
      toast.error("Failed to load timesheet data");
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'superadmin';

  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      const isProjectMatch = selectedProject === "all" || ticket.project_id === selectedProject;

      const ticketTime = ticket.resolvedDate.getTime();
      const isAfterFrom = !dateRange.from || ticketTime >= dateRange.from.setHours(0, 0, 0, 0);
      const isBeforeTo = !dateRange.to || ticketTime <= dateRange.to.setHours(23, 59, 59, 999);

      return isProjectMatch && isAfterFrom && isBeforeTo;
    });
  }, [tickets, selectedProject, dateRange]);

  const totalHours = useMemo(() => {
    return filteredTickets.reduce((sum, ticket) => sum + (parseFloat(ticket.resolved_hours) || 0), 0);
  }, [filteredTickets]);

  const handleThisMonth = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date();
    setDateRange({ from, to });
  };

  const handleLastMonth = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    setDateRange({ from, to });
  };

  const handleExportExcel = () => {
    try {
      const exportData = filteredTickets.map(ticket => {
        const project = projects.find(p => p.id === ticket.project_id);
        return {
          'Ticket Number': ticket.ticketNumber || 'N/A',
          'Ticket Title': ticket.title || 'Untitled',
          'Project': project?.name || 'Unknown Project',
          'Developer': ticket.assigned_developer_name || 'Unassigned',
          'Resolved Date': format(ticket.resolvedDate, 'yyyy-MM-dd HH:mm'),
          'Resolved Hours': ticket.resolved_hours || 0
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Append a summary total row
      XLSX.utils.sheet_add_aoa(ws, [
        [],
        ['Total Hours', '', '', '', '', totalHours]
      ], { origin: -1 });

      XLSX.utils.book_append_sheet(wb, ws, "Resolved Hours Timesheet");

      const fileName = `Timesheet_Report_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success("Timesheet report exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export timesheet");
    }
  };

  // If role is loaded but not superadmin, block render before redirect
  if (user && !isSuperAdmin) {
    return null;
  }

  return (
    <Layout user={user}>
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-5">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Resolution Timesheet</h1>
            <p className="text-muted-foreground mt-1">
              View and audit total resolved hours logged by developers across projects
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportExcel} className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Export Timesheet
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              
              {/* Project Filter */}
              <div className="space-y-2 flex-1">
                <label className="text-sm font-medium text-gray-700">Project</label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Filters */}
              <div className="space-y-2 flex-1">
                <label className="text-sm font-medium text-gray-700">Date Range</label>
                <div className="flex gap-2 items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange.from && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.from ? format(dateRange.from, "PPP") : <span>Pick start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateRange.from}
                        onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <span className="text-muted-foreground">-</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange.to && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.to ? format(dateRange.to, "PPP") : <span>Pick end date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateRange.to}
                        onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Quick Filters & Reset */}
              <div className="flex gap-2 pb-0.5">
                <Button variant="outline" onClick={handleThisMonth}>
                  This Month
                </Button>
                <Button variant="outline" onClick={handleLastMonth}>
                  Last Month
                </Button>
                <Button variant="ghost" onClick={() => {
                  setSelectedProject("all");
                  setDateRange({ from: null, to: null });
                }} className="gap-1 text-gray-500 hover:text-gray-900">
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </Button>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Total Hours Metric Card */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Hours Logged</CardTitle>
              <Clock className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{totalHours.toFixed(1)} hrs</div>
              <p className="text-xs text-muted-foreground mt-1">
                Sum of resolved hours for filtered tickets
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Tickets Resolved</CardTitle>
              <Filter className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{filteredTickets.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Total tickets matching selection
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Table list */}
        <Card>
          <CardHeader>
            <CardTitle>Resolved Tickets Details</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-muted-foreground">Loading timesheet records...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket ID</TableHead>
                    <TableHead>Ticket Title</TableHead>
                    <TableHead>Project Name</TableHead>
                    <TableHead>Developer</TableHead>
                    <TableHead>Resolved On</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map(ticket => {
                    const project = projects.find(p => p.id === ticket.project_id);
                    return (
                      <TableRow key={ticket.id}>
                        <TableCell className="font-semibold text-gray-500">#{ticket.ticketNumber || 'N/A'}</TableCell>
                        <TableCell className="font-medium text-gray-900">{ticket.title}</TableCell>
                        <TableCell className="text-gray-600">{project?.name || 'Unknown Project'}</TableCell>
                        <TableCell className="text-gray-600">{ticket.assigned_developer_name || 'Unassigned'}</TableCell>
                        <TableCell className="text-gray-600">{format(ticket.resolvedDate, 'PPP')}</TableCell>
                        <TableCell className="text-right font-medium text-gray-900">{ticket.resolved_hours || 0} hrs</TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredTickets.length > 0 && (
                    <TableRow className="bg-slate-50 font-bold border-t-2 border-gray-200">
                      <TableCell colSpan={5} className="text-left py-4 text-gray-900">Total Hours Logged</TableCell>
                      <TableCell className="text-right py-4 text-primary text-lg">{totalHours.toFixed(1)} hrs</TableCell>
                    </TableRow>
                  )}
                  {filteredTickets.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center h-24 text-muted-foreground">
                        No resolved tickets found for this selection
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
